// The Topology page's model: site -> OLT -> PON -> the ONTs that need
// attention, plus the network devices at each site. Pure, checked by
// topology.check.ts. OLT/PON numbers and OLT health are the overview's and
// olthealth's, so this page can never disagree with the OLT pages.
import { oltState, ontProblem, type OltState, type OntProblem, type Reason } from "./olthealth";
import type { SignalBands } from "./signal";
import type { Overview } from "./types";

export type TopoONT = {
  id: number; ont_id: number; serial: string; description: string;
  run_state: string | null; last_down_cause: string | null; last_down_at: string | null; rx_power_dbm: number | null;
};
export type TopoPON = { id: number; path: string; native: string; problems: TopoONT[] };
export type TopoOLT = { id: number; name: string; vendor: string; model: string; mgmt_ip: string; location: string; map_key: string; pons: TopoPON[] };
export type TopoDevice = {
  id: number; key: string; name: string; hostname: string; os: string | null; hardware: string | null;
  status: "up" | "down" | "unknown"; site: string; site_source: "librenms" | "name" | "olt" | ""; olt_id: number | null; last_polled_at: string | null;
};
export type Confidence = "confirmed" | "discovered" | "manual";
export type TopoLink = { a: number; b: number; confidence: Confidence; manual: boolean; protocols: string[]; interfaces: string[]; reported_by: number[]; updated_at: string };
export type Unresolved = "unmonitored" | "ambiguous" | "self" | "anonymous";
export type TopoUnresolved = { from: number; remote_name: string; reason: Unresolved; protocol: string; platform: string; ports: string[]; count: number; updated_at: string };
export type TopologyResponse = { olts: TopoOLT[]; devices: TopoDevice[]; links: TopoLink[]; unresolved: TopoUnresolved[]; generated_at: string };

/** One scale for every level. Weak optics warn; they never degrade an OLT. */
export type NodeState = "critical" | "down" | "degraded" | "stale" | "warn" | "unknown" | "healthy";
export const STATE_RANK: Record<NodeState, number> = { down: 0, critical: 1, degraded: 2, stale: 3, warn: 4, unknown: 5, healthy: 6 };
export const worst = (xs: NodeState[], empty: NodeState = "healthy"): NodeState =>
  xs.length ? xs.reduce((a, b) => (STATE_RANK[b] < STATE_RANK[a] ? b : a)) : empty;

export type ONTNode = TopoONT & { problem: OntProblem };
export type PONNode = {
  id: number; oltId: number; path: string; native: string;
  total: number; online: number; weak: number; lastDownAt: string | null;
  state: NodeState; problems: ONTNode[];
};
export type OLTNode = {
  id: number; name: string; vendor: string; model: string; ip: string; mapKey: string; site: string;
  status: string; lastPolledAt: string | null; tempC: number | null; alerts: number;
  total: number; online: number; state: NodeState; reasons: Reason[]; pons: PONNode[];
};
export type DeviceNode = TopoDevice & { state: NodeState; links: number };
export type SiteNode = {
  name: string; // "" = not assigned to a site
  state: NodeState; olts: OLTNode[]; devices: DeviceNode[];
  /** Whole-site counts: problemsOnly prunes the lists, never these. */
  oltCount: number; deviceCount: number; pons: number; total: number; online: number;
};

// OLT states map 1:1 onto the node scale.
const fromOlt = (s: OltState): NodeState => s;

/** PON tier, as the OLT page reads it: a dark PON is down, any offline ONT degrades it, weak optics warn. */
export function ponState(total: number, online: number, weak: number): NodeState {
  if (total >= 2 && online === 0) return "down";
  if (online < total) return "degraded";
  if (weak > 0) return "warn";
  return "healthy";
}

export function buildTree(topo: TopologyResponse, ov: Pick<Overview, "olts" | "pons" | "olt_poll_interval_s">, bands: SignalBands, nowMs: number): SiteNode[] {
  const pollS = ov.olt_poll_interval_s || 120;
  const linkCount = new Map<number, number>();
  for (const l of topo.links) for (const id of [l.a, l.b]) linkCount.set(id, (linkCount.get(id) ?? 0) + 1);

  const olts: OLTNode[] = topo.olts.map((o) => {
    const live = ov.olts.find((x) => x.id === o.id);
    const ovPons = ov.pons.filter((p) => p.olt_id === o.id);
    const pons: PONNode[] = o.pons.map((p) => {
      const agg = ovPons.find((x) => x.id === p.id);
      const total = agg?.onts_total ?? 0, online = agg?.onts_online ?? 0, weak = agg?.weak ?? 0;
      const problems = p.problems
        .map((x) => ({ ...x, problem: ontProblem(x, bands) }))
        .filter((x): x is ONTNode => x.problem !== null);
      return { id: p.id, oltId: o.id, path: p.path, native: p.native, total, online, weak,
        lastDownAt: agg?.last_down_at ?? null, state: ponState(total, online, weak), problems };
    });
    const { state, reasons } = oltState(
      { status: live?.status ?? "unknown", last_polled_at: live?.last_polled_at ?? null,
        onts_total: live?.onts_total ?? 0, onts_online: live?.onts_online ?? 0, temp_c: live?.temp_c ?? null },
      pons.map((p) => ({ onts_total: p.total, onts_online: p.online })), pollS, nowMs);
    // Weak optics under a healthy OLT still point the way down the tree.
    const s = fromOlt(state) === "healthy" && pons.some((p) => p.state === "warn") ? "warn" : fromOlt(state);
    return {
      id: o.id, name: o.name, vendor: o.vendor, model: o.model, ip: o.mgmt_ip, mapKey: o.map_key, site: o.location,
      status: live?.status ?? "unknown", lastPolledAt: live?.last_polled_at ?? null, tempC: live?.temp_c ?? null,
      alerts: (live?.alerts_critical ?? 0) + (live?.alerts_warn ?? 0),
      total: live?.onts_total ?? 0, online: live?.onts_online ?? 0, state: s, reasons, pons,
    };
  });
  // OLTs are drawn under the access network; their LibreNMS twin is not repeated.
  const devices: DeviceNode[] = topo.devices.filter((d) => d.olt_id === null).map((d) => ({
    ...d, links: linkCount.get(d.id) ?? 0,
    state: d.status === "down" ? "degraded" : d.status === "unknown" ? "unknown" : "healthy",
  }));

  const names = [...new Set([...olts.map((o) => o.site), ...devices.map((d) => d.site)])];
  const sites = names.map((name): SiteNode => {
    const so = olts.filter((o) => o.site === name);
    const sd = devices.filter((d) => d.site === name);
    // A down or critical OLT makes its site critical; one dead ONT only
    // degrades its PON and OLT, never the whole site.
    const oltWorst = worst(so.map((o) => o.state));
    const state: NodeState = oltWorst === "down" || oltWorst === "critical" ? "critical"
      : worst([oltWorst, ...sd.map((d) => (d.state === "degraded" ? "degraded" as NodeState : "healthy" as NodeState))]);
    return {
      name, state, olts: so, devices: sd, oltCount: so.length, deviceCount: sd.length,
      pons: so.reduce((n, o) => n + o.pons.length, 0),
      total: so.reduce((n, o) => n + o.total, 0), online: so.reduce((n, o) => n + o.online, 0),
    };
  });
  // Worst site first, the unassigned bucket last.
  return sites.sort((a, b) => (a.name === "") !== (b.name === "") ? (a.name === "" ? 1 : -1)
    : STATE_RANK[a.state] - STATE_RANK[b.state] || a.name.localeCompare(b.name));
}

/** "Problems only": keep what is not healthy, and the path down to it. */
export function problemsOnly(sites: SiteNode[]): SiteNode[] {
  return sites.map((s) => ({
    ...s,
    olts: s.olts.filter((o) => o.state !== "healthy").map((o) => ({ ...o, pons: o.pons.filter((p) => p.state !== "healthy") })),
    devices: s.devices.filter((d) => d.state !== "healthy"),
  })).filter((s) => s.olts.length || s.devices.length);
}

// --- identity ---------------------------------------------------------------

const PLATFORM: Record<string, string> = {
  routeros: "MikroTik RouterOS", arista_eos: "Arista EOS", vrp: "Huawei VRP", nxos: "Cisco NX-OS", ios: "Cisco IOS",
  iosxe: "Cisco IOS-XE", junos: "Juniper Junos", dsm: "Synology DSM", linux: "Linux", cdata: "C-Data", "parks-switch": "Parks",
};
/** "MikroTik RouterOS", never the raw LibreNMS os slug. */
export const platformName = (os: string | null) => (os ? PLATFORM[os] ?? os : "");

// --- search -----------------------------------------------------------------

export type NodeRef =
  | { kind: "site"; site: string }
  | { kind: "olt"; id: number }
  | { kind: "pon"; id: number }
  | { kind: "ont"; id: number; ponId: number }
  | { kind: "device"; id: number };

export type Hit = { ref: NodeRef; label: string; sub: string; path: string[]; state: NodeState };
/** An ONT from the server-side ONT search (the tree only carries problem ONTs). */
export type OntHit = { id: number; ont_id: number; serial: string; description: string; olt_id: number; pon_path: string; run_state: string | null };

export const refKey = (r: NodeRef) => r.kind === "site" ? `site:${r.site}` : `${r.kind}:${r.id}`;

export function search(sites: SiteNode[], q: string, onts: OntHit[] = []): Hit[] {
  const n = q.trim().toLowerCase();
  if (!n) return [];
  const has = (...xs: (string | null | undefined)[]) => xs.some((x) => x?.toLowerCase().includes(n));
  const hits: Hit[] = [];
  const seenOnt = new Set<number>();
  for (const s of sites) {
    const sp = s.name || "—";
    if (s.name && has(s.name)) hits.push({ ref: { kind: "site", site: s.name }, label: s.name, sub: "", path: [], state: s.state });
    for (const o of s.olts) {
      if (has(o.name, o.ip, o.model)) hits.push({ ref: { kind: "olt", id: o.id }, label: o.name, sub: o.ip, path: [sp], state: o.state });
      for (const p of o.pons) {
        if (has(p.path, p.native) && n.length >= 3) hits.push({ ref: { kind: "pon", id: p.id }, label: `PON ${p.path}`, sub: p.native, path: [sp, o.name], state: p.state });
        for (const x of p.problems) {
          if (has(x.description, x.serial)) {
            seenOnt.add(x.id);
            hits.push({ ref: { kind: "ont", id: x.id, ponId: p.id }, label: x.description || x.serial, sub: `ONT #${x.ont_id} · ${x.serial}`, path: [sp, o.name, `PON ${p.path}`], state: x.problem.sev === "crit" ? "critical" : "warn" });
          }
        }
      }
    }
    for (const d of s.devices) {
      if (has(d.name, d.hostname, d.hardware)) hits.push({ ref: { kind: "device", id: d.id }, label: d.name, sub: d.hostname, path: [sp], state: d.state });
    }
  }
  // Healthy ONTs come from the server search: place each under its PON.
  for (const x of onts) {
    if (seenOnt.has(x.id)) continue;
    for (const s of sites) for (const o of s.olts) {
      if (o.id !== x.olt_id) continue;
      const p = o.pons.find((pp) => pp.path === x.pon_path);
      if (!p) continue;
      hits.push({ ref: { kind: "ont", id: x.id, ponId: p.id }, label: x.description || x.serial, sub: `ONT #${x.ont_id} · ${x.serial}`,
        path: [s.name || "—", o.name, `PON ${p.path}`], state: x.run_state === "Online" ? "healthy" : "critical" });
    }
  }
  return hits.slice(0, 30);
}

/** Every tree node that must be open for `ref` to be visible. */
export function pathTo(sites: SiteNode[], ref: NodeRef): string[] {
  for (const s of sites) {
    const site = `site:${s.name}`;
    if (ref.kind === "site" && ref.site === s.name) return [site];
    if (ref.kind === "device" && s.devices.some((d) => d.id === ref.id)) return [site, `devs:${s.name}`];
    for (const o of s.olts) {
      if (ref.kind === "olt" && ref.id === o.id) return [site];
      const ponId = ref.kind === "pon" ? ref.id : ref.kind === "ont" ? ref.ponId : -1;
      if (o.pons.some((p) => p.id === ponId)) return ref.kind === "ont" ? [site, `olt:${o.id}`, `pon:${ponId}`] : [site, `olt:${o.id}`];
    }
  }
  return [];
}

// --- discovery ----------------------------------------------------------------

export type DiscoveryCounts = { devices: number; links: number; confirmed: number; discovered: number; manual: number; unresolved: number; neighbours: number };

export function discoveryCounts(t: TopologyResponse): DiscoveryCounts {
  const c = { devices: t.devices.length, links: t.links.length, confirmed: 0, discovered: 0, manual: 0, unresolved: t.unresolved.length, neighbours: 0 };
  for (const l of t.links) c[l.confidence]++;
  c.neighbours = t.unresolved.filter((u) => u.reason === "unmonitored").length;
  return c;
}
